/**
 * Task #1435 — hook React per leggere i limiti d'uso del tenant.
 *
 * Fonte dati: `GET /tenant/usage-limits` (rotta protetta tenant-from-JWT).
 * Espone counts/limits/status per i 3 tier: voip, users, ai_agents.
 *
 * Si auto-aggiorna quando l'apiClient emette `api:tenant-limit-reached`,
 * così il banner riflette subito la nuova soglia dopo il fallimento HTTP 403.
 */
import { useCallback, useEffect, useState } from 'react';
import type { TenantLimitsResponse, TenantLimitStatus } from '@easyflow/shared';
import { apiClient, isApiError } from '@/lib/api';
import { TENANT_LIMIT_EVENT } from '@/lib/tenantLimits';

export type TenantUsageTier = 'voip' | 'users' | 'ai_agents';

export interface TenantUsageState {
  data: TenantLimitsResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

interface ApiEnvelope<T> {
  success?: boolean;
  data?: T;
}

function unwrap(payload: unknown): TenantLimitsResponse | null {
  if (!payload || typeof payload !== 'object') return null;
  const env = payload as ApiEnvelope<TenantLimitsResponse>;
  if (env.data && typeof env.data === 'object' && 'voip' in env.data) {
    return env.data as TenantLimitsResponse;
  }
  if ('voip' in (payload as Record<string, unknown>)) {
    return payload as TenantLimitsResponse;
  }
  return null;
}

export function useTenantUsage(): TenantUsageState {
  const [data, setData] = useState<TenantLimitsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<TenantLimitsResponse>('/tenant/usage-limits');
      const parsed = unwrap(res);
      if (!parsed) {
        setError('Risposta limiti tenant non valida');
        return;
      }
      setData(parsed);
    } catch (err: unknown) {
      const msg = isApiError(err) ? err.message : 'Errore caricamento limiti tenant';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Auto-refresh quando un 403 di limite tenant viene osservato altrove.
  useEffect(() => {
    const handler = (): void => { void refresh(); };
    window.addEventListener(TENANT_LIMIT_EVENT, handler);
    return () => window.removeEventListener(TENANT_LIMIT_EVENT, handler);
  }, [refresh]);

  return { data, loading, error, refresh };
}

export function tierStatus(
  data: TenantLimitsResponse | null,
  tier: TenantUsageTier,
): TenantLimitStatus | null {
  if (!data) return null;
  return data[tier].status;
}
